import React, { useEffect, useState, useMemo } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAttendanceActions } from '_actions/attendance.actions';
import { homeworkAverage, homeworkWeight } from '_helpers/homework';
import dayjs from '_helpers/datetime';

export { HomeworkChart };

/**
 * Line chart of homework-completion % over sessions, plus an overall summary number.
 * Self-loads sessions for `classId`. For staff the per-session value is the class average;
 * for a student (sessions are pre-filtered to their own record) it's their own value.
 */
function HomeworkChart({ classId, title = '% hoàn thành bài tập về nhà' }) {
    const attendanceActions = useAttendanceActions();
    const [sessions, setSessions] = useState([]);

    useEffect(() => {
        let alive = true;
        if (!classId) return;
        attendanceActions.getSessions(classId).then(data => {
            if (alive) setSessions(Array.isArray(data) ? data : []);
        });
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId]);

    const { points, summary } = useMemo(() => {
        const ordered = [...sessions].sort((a, b) => new Date(a.date) - new Date(b.date));
        const pts = ordered.map(s => {
            const codes = (s.records || []).map(r => r.homework);
            const avg = homeworkAverage(codes); // null if nothing graded that session
            return {
                name: s.lesson_name || (s.date ? dayjs(s.date).format('DD/MM') : ''),
                value: avg === null ? null : Math.round(avg),
            };
        });
        // Overall = mean of every graded record across all sessions
        const allWeights = [];
        sessions.forEach(s => (s.records || []).forEach(r => {
            const w = homeworkWeight(r.homework);
            if (w !== null) allWeights.push(w);
        }));
        const overall = allWeights.length
            ? Math.round(allWeights.reduce((a, b) => a + b, 0) / allWeights.length)
            : null;
        return { points: pts, summary: overall };
    }, [sessions]);

    const hasData = points.some(p => p.value !== null);

    return (
        <div className="att2-metric" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>{title}</span>
                <span>
                    <span style={{ color: 'var(--ink-4)', fontSize: 13, marginRight: 6 }}>Tổng kết:</span>
                    <span style={{ fontSize: 20, fontWeight: 700, color: '#52B6CE' }}>
                        {summary === null ? '—' : `${summary}%`}
                    </span>
                </span>
            </div>
            <div style={{ width: '100%', height: 260, marginTop: 12 }}>
                {hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                            <Tooltip formatter={v => `${v}%`} />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#52B6CE"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                connectNulls
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--ink-4)' }}>
                        Chưa có dữ liệu bài tập về nhà
                    </div>
                )}
            </div>
        </div>
    );
}
